/**
 * Shared visual language for the Study Deck surfaces (Hub, Session,
 * Duel, Concept Map). Single source of truth so all four screens are
 * pixel-consistent — the whole "consistent UI design" ask from the
 * user rides on this file.
 *
 * Palette + typography taken directly from /app/design_guidelines.json.
 */
export const STUDY_THEME = {
  gold:      '#FCD359',
  goldDark:  '#E0B73D',
  goldDeep:  '#8C6C1F',
  ink:       '#0E2A47',
  inkSoft:   '#1F3A5C',
  paper:     '#FDFAF1',
  paperWarm: '#F4ECD0',
  line:      'rgba(14,42,71,0.10)',
  muted:     '#6B7280',
  shadow:    '0 8px 30px rgba(14,42,71,0.08)',
};

// Per-card-type accent + label + emoji (kept in one place so the badge,
// border, and reveal-header colors all match).
export const CARD_TYPE_META = {
  standard: {
    accent:  '#0E2A47',
    bg:      'rgba(14,42,71,0.05)',
    label:   'Standard',
    emoji:   '📘',
    tooltip: 'Baseline facts and vocabulary.',
  },
  confused: {
    accent:  '#F97316',
    bg:      'rgba(249,115,22,0.08)',
    label:   'Frequently Confused',
    emoji:   '🔥',
    tooltip: 'A card students most often get wrong. Slow down.',
  },
  bridge: {
    accent:  '#7C3AED',
    bg:      'rgba(124,58,237,0.08)',
    label:   'Concept Bridge',
    emoji:   '🌉',
    tooltip: 'Connects concepts across modules.',
  },
  cloze: {
    accent:  '#8C6C1F',
    bg:      'rgba(140,108,31,0.08)',
    label:   'Fill in the Blank',
    emoji:   '✍️',
    tooltip: 'Reconstruct the missing terms.',
  },
  feynman: {
    accent:  '#10B981',
    bg:      'rgba(16,185,129,0.08)',
    label:   'Free Recall',
    emoji:   '💡',
    tooltip: 'Explain in your own words. AI grades your answer.',
  },
};

export const RATING_META = [
  { rating: 'again', label: 'Again',  key: '1', accent: '#DC2626', bg: '#FEE2E2', hint: 'Show me sooner' },
  { rating: 'hard',  label: 'Hard',   key: '2', accent: '#EA580C', bg: '#FED7AA', hint: 'Slight setback' },
  { rating: 'good',  label: 'Good',   key: '3', accent: '#0E2A47', bg: '#DBEAFE', hint: 'Normal recall' },
  { rating: 'easy',  label: 'Easy',   key: '4', accent: '#16A34A', bg: '#BBF7D0', hint: 'Longer interval' },
];

// Utility: wrap a page in the paper-textured warm background that
// signals "study mode" and separates it visually from the SaaS chrome.
export const paperBackground = () => ({
  minHeight: '100vh',
  background: `linear-gradient(135deg, ${STUDY_THEME.paper} 0%, ${STUDY_THEME.paperWarm} 100%)`,
  fontFamily: '"IBM Plex Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  color: STUDY_THEME.ink,
});
